import { createSelector } from 'reselect';
import { isNil } from '../../../../util/object';
import { isNumber } from '../../../../util/numbers';
import { getCurrentFrame } from '../../../playback/manager/PlaybackManager';
import {
  getAllElementsById,
  isVisibleOnCanvas,
} from '../../../../selectors/element';
import { getNearestKeyFrames } from '../../../../util/keyframe';

export const getEditorSelection = (state) => state.editor.selection;

export const getSelectedConstraintElementId = createSelector(
  [getEditorSelection],
  (editorSelection) => {
    if (isNil(editorSelection)) return undefined;
    return editorSelection.constraintElementId;
  },
);

export const getSelectedDisplayElementId = createSelector(
  [getEditorSelection],
  (editorSelection) => {
    if (isNil(editorSelection)) return undefined;
    return editorSelection.displayElementId;
  },
);

export const getSelectedTransformElementId = createSelector(
  [getEditorSelection],
  (editorSelection) => {
    if (isNil(editorSelection)) return undefined;
    return editorSelection.transformElementId;
  },
);

// This is exclusive because you want to be able to edit the transform if the currentFrame === start/end frame of pan segment
export const getIsSelectedFrameInPanSegmentExclusive = createSelector(
  [getCurrentFrame, getSelectedDisplayElementId, getAllElementsById],
  (currentFrame, selectedElementId, elementMap) => {
    if (isNil(selectedElementId)) return false;
    const selectedElement = elementMap[selectedElementId];
    if (isNil(selectedElement)) return false;
    const { transforms } = selectedElement;
    const transitionKeys = Object.keys(transforms).map((frame) =>
      parseInt(frame, 10),
    );
    // if there is one transform, the selected element is not in 'pan' state
    if (transitionKeys.length < 2) return false;
    const { leftKeyFrame, rightKeyFrame } = getNearestKeyFrames(
      transitionKeys,
      currentFrame,
    );
    const isLargerThanLeftBound =
      isNumber(leftKeyFrame) && currentFrame > leftKeyFrame;
    const isSmallerThanRightBound =
      isNumber(rightKeyFrame) && currentFrame < rightKeyFrame;
    return isLargerThanLeftBound && isSmallerThanRightBound;
  },
);

export const isStageSelected = (state) => {
  const selectedTransformElementId = getSelectedTransformElementId(state);
  const currentFrame = getCurrentFrame();
  if (
    isNil(selectedTransformElementId) ||
    getIsSelectedFrameInPanSegmentExclusive(state)
  ) {
    return true;
  }
  return !isVisibleOnCanvas(state, selectedTransformElementId, currentFrame);
};
